import React, { useState, useEffect, useCallback } from "react";
import {
  ComposedChart, Bar, Line, XAxis, YAxis, CartesianGrid, Tooltip,
  ResponsiveContainer, ScatterChart, Scatter, ZAxis, ReferenceLine, Cell,
} from "recharts";
import "./EventAnalysis.css";

const API = process.env.REACT_APP_API_URL;

const SITES = [
  { site_id: 5,  label: "Planter 006" },
  { site_id: 11, label: "Planter 007A" },
];

const MODE_CFG = {
  STORE:      { color: "#0077ff", bg: "#eff6ff", icon: "💧", label: "Store" },
  INFILTRATE: { color: "#16a34a", bg: "#f0fdf4", icon: "🌱", label: "Infiltrate" },
  RELEASE:    { color: "#f59e0b", bg: "#fffbeb", icon: "⚠️", label: "Release" },
};

const SM_COLORS   = ["#bfdbfe", "#93c5fd", "#60a5fa", "#3b82f6", "#2563eb", "#1d4ed8"];
const EC_COLORS   = ["#fdba74", "#fb923c", "#f97316", "#ea580c", "#c2410c", "#9a3412"];
const TEMP_COLORS = ["#ef4444", "#f97316", "#eab308", "#84cc16", "#22c55e", "#06b6d4"];
const SM_DEPTHS = [10, 20, 30, 40, 50, 60];

function fmtDate(s) {
  if (!s) return "—";
  return new Date(s).toLocaleDateString("en-GB", {
    day: "2-digit", month: "short", year: "numeric",
  });
}

function fmtDatetime(s) {
  if (!s) return "—";
  return new Date(s).toLocaleString("en-GB", {
    day: "2-digit", month: "short", hour: "2-digit", minute: "2-digit",
  });
}

function fmtMonth(s) {
  if (!s) return s;
  const [year, mon] = s.split("-");
  return new Date(+year, +mon - 1, 1).toLocaleDateString("en-GB", { month: "short", year: "2-digit" });
}

function smColor(v, minV, maxV) {
  if (v === null || v === undefined) return "#f3f4f6";
  const t = maxV > minV ? Math.max(0, Math.min(1, (v - minV) / (maxV - minV))) : 0.5;
  const l = Math.round(96 - t * 56);
  const s = Math.round(40 + t * 43);
  return `hsl(221,${s}%,${l}%)`;
}

function tempColor(v, minV, maxV) {
  if (v === null || v === undefined) return "#f3f4f6";
  const t = maxV > minV ? Math.max(0, Math.min(1, (v - minV) / (maxV - minV))) : 0.5;
  // cool blue hsl(220,60%,88%) → warm orange-red hsl(15,85%,52%)
  const h = Math.round(220 - t * 205);
  const s = Math.round(60 + t * 25);
  const l = Math.round(88 - t * 36);
  return `hsl(${h},${s}%,${l}%)`;
}

// ── Cumulative performance card ────────────────────────────────────────────────

function CumulativeTile({ value, unit, label, sub }) {
  return (
    <div className="ea-cum-tile">
      <div className="ea-cum-value">
        {value ?? "—"}
        {unit && <span className="ea-cum-unit">{unit}</span>}
      </div>
      <div className="ea-cum-label">{label}</div>
      {sub && <div className="ea-cum-sub">{sub}</div>}
    </div>
  );
}

function CumulativeCard({ stats }) {
  if (!stats) return null;
  return (
    <div className="ea-cumulative">
      <CumulativeTile
        value={stats.event_count}
        label="Events detected"
        sub={stats.first_event && stats.last_event ? `${stats.first_event} – ${stats.last_event}` : null}
      />
      <CumulativeTile
        value={stats.mean_efficiency_pct}
        unit="%"
        label="Mean efficiency"
        sub="rainfall retained"
      />
      <CumulativeTile
        value={stats.peak_efficiency_pct}
        unit="%"
        label="Peak efficiency"
        sub="best single event"
      />
      <CumulativeTile
        value={stats.months_monitored}
        label="Months monitored"
        sub={stats.first_event && stats.last_event ? `${stats.first_event} – ${stats.last_event}` : null}
      />
    </div>
  );
}

// ── Recommendation card ────────────────────────────────────────────────────────

function RecommendationCard({ rec }) {
  if (!rec || rec.error) {
    return (
      <div className="ea-card ea-rec-card">
        <div className="ea-loading">{rec?.error || "Loading recommendation…"}</div>
      </div>
    );
  }
  const cfg = MODE_CFG[rec.mode] || MODE_CFG.STORE;
  return (
    <div className="ea-card ea-rec-card" style={{ borderLeft: `4px solid ${cfg.color}`, background: cfg.bg }}>
      <div className="ea-rec-header">
        <span className="ea-rec-mode" style={{ color: cfg.color }}>
          {cfg.icon} {cfg.label}
        </span>
        <span className="ea-rec-conf" style={{ color: cfg.color }}>
          {rec.confidence} confidence
        </span>
      </div>
      <p className="ea-rec-reason">{rec.reasoning}</p>
      <div className="ea-metrics-row">
        {[
          { val: `${rec.avg_sm_pct}%`,           lbl: "Avg. moisture" },
          { val: `${rec.current_water_l} L`,      lbl: "Stored" },
          { val: `${rec.available_capacity_l} L`, lbl: "Available" },
          { val: `${rec.forecast_24h_mm} mm`,     lbl: "24 h forecast" },
          { val: `${rec.forecast_prob_pct}%`,      lbl: "Rain probability" },
        ].map(m => (
          <div key={m.lbl} className="ea-metric">
            <span className="ea-metric-val">{m.val}</span>
            <span className="ea-metric-lbl">{m.lbl}</span>
          </div>
        ))}
      </div>
    </div>
  );
}

// ── 48 h forecast strip ────────────────────────────────────────────────────────

function ForecastStrip({ forecast }) {
  if (!forecast || !forecast.length) return null;
  const hours = forecast.slice(0, 48);
  const maxRain = Math.max(...hours.map(h => h.rain), 0.5);
  return (
    <div className="ea-card ea-forecast-card">
      <div className="ea-card-title">48 h Forecast — Hull</div>
      <ResponsiveContainer width="100%" height={90}>
        <ComposedChart data={hours} margin={{ top: 4, right: 4, left: -28, bottom: 0 }}>
          <XAxis
            dataKey="time"
            tickFormatter={t => `${new Date(t).getHours()}h`}
            interval={7}
            tick={{ fontSize: 10 }}
          />
          <YAxis yAxisId="r" domain={[0, maxRain * 1.3]} hide />
          <YAxis yAxisId="p" domain={[0, 100]} hide />
          <Tooltip
            labelFormatter={t => fmtDatetime(t)}
            formatter={(v, n) => [v, n === "rain" ? "Rain (mm)" : "Probability (%)"]}
          />
          <Bar yAxisId="r" dataKey="rain" fill="#93c5fd" name="rain" radius={[2, 2, 0, 0]} />
          <Line yAxisId="p" dataKey="prob" stroke="#0077ff" dot={false} strokeWidth={1.5} name="prob" />
        </ComposedChart>
      </ResponsiveContainer>
      <div className="ea-fc-legend">
        <span><span className="ea-fc-swatch" style={{ background: "#93c5fd" }} /> Rain (mm)</span>
        <span><span className="ea-fc-swatch ea-fc-line" style={{ background: "#0077ff" }} /> Probability (%)</span>
      </div>
    </div>
  );
}

// ── Shared download utility ────────────────────────────────────────────────────

function downloadCSV(filename, rows) {
  const blob = new Blob([rows.join("\n")], { type: "text/csv" });
  const url  = URL.createObjectURL(blob);
  const a    = document.createElement("a");
  a.href = url; a.download = filename; a.click();
  URL.revokeObjectURL(url);
}

// ── Event list ─────────────────────────────────────────────────────────────────

function EventList({ events, selectedId, onSelect, onDetect, detecting, siteId }) {
  return (
    <div className="ea-card ea-events-card">
      <div className="ea-card-header">
        <div className="ea-card-title">Storm Events ({events.length})</div>
        <div style={{ display: "flex", gap: 6 }}>
          <a
            className="ea-btn-sm"
            href={`${API}/hydro/events/export?site_id=${siteId}`}
            download
          >
            CSV
          </a>
          <button className="ea-btn-sm" onClick={onDetect} disabled={detecting}>
            {detecting ? "Scanning…" : "Refresh"}
          </button>
        </div>
      </div>
      {events.length === 0 ? (
        <div className="ea-empty">No events yet — click Refresh to scan historical data.</div>
      ) : (
        <div className="ea-event-list">
          {events.map(ev => (
            <div
              key={ev.id}
              className={`ea-event-row${selectedId === ev.id ? " selected" : ""}`}
              onClick={() => onSelect(ev.id)}
            >
              <div className="ea-event-date">{fmtDate(ev.event_start)}</div>
              <div className="ea-event-pills">
                <span className="ea-pill">{ev.total_rainfall_mm} mm</span>
                <span className="ea-pill">{ev.duration_hours} h</span>
                <span className="ea-pill">{ev.peak_intensity_mmhr} mm/hr peak</span>
              </div>
              <div className="ea-event-adp">ADP: {ev.antecedent_dry_days} d dry</div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

// ── SM heatmap ─────────────────────────────────────────────────────────────────

function SMHeatmap({ timeseries }) {
  if (!timeseries || timeseries.length === 0) return null;

  const allVals = SM_DEPTHS.flatMap(d =>
    timeseries.map(r => r[`sm_${d}`]).filter(v => v !== null && v !== undefined)
  );
  const minV = allVals.length ? Math.min(...allVals) : 0;
  const maxV = allVals.length ? Math.max(...allVals) : 100;

  const maxRain = Math.max(...timeseries.map(r => r.rain || 0), 0.1);
  const N = timeseries.length;
  const tickStep = Math.max(1, Math.ceil(N / 5));

  return (
    <div className="ea-heatmap-wrap">

      {/* Rain strip */}
      <div className="ea-heatmap-row">
        <div className="ea-heatmap-label">Rain</div>
        <div className="ea-heatmap-cells ea-heatmap-rain-strip">
          {timeseries.map((r, i) => (
            <div key={i} className="ea-heatmap-rain-cell" title={`${fmtDatetime(r.time)}: ${(r.rain || 0).toFixed(1)} mm`}>
              <div
                className="ea-heatmap-rain-bar"
                style={{ height: `${((r.rain || 0) / maxRain) * 100}%` }}
              />
            </div>
          ))}
        </div>
      </div>

      {/* Depth rows */}
      {SM_DEPTHS.map(d => (
        <div key={d} className="ea-heatmap-row">
          <div className="ea-heatmap-label">{d} cm</div>
          <div className="ea-heatmap-cells">
            {timeseries.map((r, i) => {
              const v = r[`sm_${d}`];
              return (
                <div
                  key={i}
                  className="ea-heatmap-cell"
                  style={{ background: smColor(v, minV, maxV) }}
                  title={v !== null && v !== undefined
                    ? `${fmtDatetime(r.time)}: ${v.toFixed(1)}%`
                    : `${fmtDatetime(r.time)}: no data`}
                />
              );
            })}
          </div>
        </div>
      ))}

      {/* Time axis */}
      <div className="ea-heatmap-row ea-heatmap-time-row">
        <div className="ea-heatmap-label" />
        <div className="ea-heatmap-cells" style={{ position: "relative", height: 28 }}>
          {timeseries.map((r, i) => (
            i % tickStep === 0 ? (
              <div
                key={i}
                className="ea-heatmap-tick"
                style={{ left: `${(i / N) * 100}%` }}
              >
                {fmtDatetime(r.time)}
              </div>
            ) : null
          ))}
        </div>
      </div>

      {/* Colour scale */}
      <div className="ea-heatmap-legend">
        <span>{minV.toFixed(1)}%</span>
        <div className="ea-heatmap-scale" />
        <span>{maxV.toFixed(1)}% SM</span>
        <span className="ea-heatmap-nodata-key" />
        <span>No data</span>
      </div>

    </div>
  );
}

// ── Soil temperature heatmap ───────────────────────────────────────────────────

function TempHeatmap({ timeseries }) {
  if (!timeseries || timeseries.length === 0) return null;

  const hasTempData = SM_DEPTHS.some(d =>
    timeseries.some(r => r[`temp_${d}`] !== null && r[`temp_${d}`] !== undefined)
  );
  if (!hasTempData) return <div className="ea-empty">No temperature data for this event.</div>;

  const allVals = SM_DEPTHS.flatMap(d =>
    timeseries.map(r => r[`temp_${d}`]).filter(v => v !== null && v !== undefined)
  );
  const minV = allVals.length ? Math.min(...allVals) : 0;
  const maxV = allVals.length ? Math.max(...allVals) : 20;

  const maxRain = Math.max(...timeseries.map(r => r.rain || 0), 0.1);
  const N = timeseries.length;
  const tickStep = Math.max(1, Math.ceil(N / 5));

  return (
    <div className="ea-heatmap-wrap">

      {/* Rain strip */}
      <div className="ea-heatmap-row">
        <div className="ea-heatmap-label">Rain</div>
        <div className="ea-heatmap-cells ea-heatmap-rain-strip">
          {timeseries.map((r, i) => (
            <div key={i} className="ea-heatmap-rain-cell" title={`${fmtDatetime(r.time)}: ${(r.rain || 0).toFixed(1)} mm`}>
              <div className="ea-heatmap-rain-bar" style={{ height: `${((r.rain || 0) / maxRain) * 100}%` }} />
            </div>
          ))}
        </div>
      </div>

      {/* Depth rows */}
      {SM_DEPTHS.map(d => (
        <div key={d} className="ea-heatmap-row">
          <div className="ea-heatmap-label">{d} cm</div>
          <div className="ea-heatmap-cells">
            {timeseries.map((r, i) => {
              const v = r[`temp_${d}`];
              return (
                <div
                  key={i}
                  className="ea-heatmap-cell"
                  style={{ background: tempColor(v, minV, maxV) }}
                  title={v !== null && v !== undefined
                    ? `${fmtDatetime(r.time)}: ${v.toFixed(1)}°C`
                    : `${fmtDatetime(r.time)}: no data`}
                />
              );
            })}
          </div>
        </div>
      ))}

      {/* Time axis */}
      <div className="ea-heatmap-row ea-heatmap-time-row">
        <div className="ea-heatmap-label" />
        <div className="ea-heatmap-cells" style={{ position: "relative", height: 28 }}>
          {timeseries.map((r, i) => (
            i % tickStep === 0 ? (
              <div key={i} className="ea-heatmap-tick" style={{ left: `${(i / N) * 100}%` }}>
                {fmtDatetime(r.time)}
              </div>
            ) : null
          ))}
        </div>
      </div>

      {/* Colour scale */}
      <div className="ea-heatmap-legend">
        <span>{minV.toFixed(1)}°C</span>
        <div className="ea-heatmap-scale ea-heatmap-scale-temp" />
        <span>{maxV.toFixed(1)}°C</span>
        <span className="ea-heatmap-nodata-key" />
        <span>No data</span>
      </div>

    </div>
  );
}

// ── Water balance bars ─────────────────────────────────────────────────────────

function WaterBalance({ balance }) {
  if (!balance) return null;
  const { rainfall_l, storage_change_l, infiltration_l, et_l, runoff_l, efficiency_pct } = balance;
  const components = [
    { label: "Storage change",       value: storage_change_l, color: "#3b82f6" },
    { label: "Infiltration",         value: infiltration_l,   color: "#16a34a" },
    { label: "Evapotranspiration",   value: et_l,             color: "#f59e0b" },
    { label: "Runoff",               value: runoff_l,          color: "#ef4444" },
  ];
  return (
    <div className="ea-balance">
      <div className="ea-balance-title">Water Balance — Input: {rainfall_l} L</div>
      {components.map(c => (
        <div key={c.label} className="ea-balance-row">
          <div className="ea-balance-label">{c.label}</div>
          <div className="ea-balance-track">
            <div
              className="ea-balance-bar"
              style={{
                width: `${Math.max(1, (c.value / rainfall_l) * 100)}%`,
                background: c.color,
              }}
            />
          </div>
          <div className="ea-balance-val">{c.value} L</div>
        </div>
      ))}
      <div className="ea-efficiency">
        Runoff reduction efficiency: <strong>{efficiency_pct}%</strong>
      </div>
    </div>
  );
}

// ── Flow hydrograph panel ──────────────────────────────────────────────────────

function FlowPanel({ eventId, siteId }) {
  const [data, setData]       = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    if (!eventId) return;
    setLoading(true);
    setData(null);
    fetch(`${API}/hydro/events/${eventId}/flow?site_id=${siteId}`)
      .then(r => r.json())
      .then(d => { setData(d); setLoading(false); })
      .catch(() => setLoading(false));
  }, [eventId, siteId]);

  if (loading) return <div className="ea-loading">Loading flow data…</div>;

  const hasData = data && data.timeseries &&
    data.timeseries.some(r => r.flow_l !== null && r.flow_l !== undefined);

  if (!hasData) {
    return (
      <div className="ea-empty">
        No flow sensor data available for this event.
        Flow monitoring started August 2023 — earlier events will not have hydrograph data.
      </div>
    );
  }

  const { timeseries, metrics } = data;
  const maxRain = Math.max(...timeseries.map(r => r.rain || 0), 0.5);
  const maxFlow = Math.max(...timeseries.map(r => r.flow_l || 0), 1);

  return (
    <div>
      {metrics && (
        <div className="ea-metrics-row ea-metrics-wrap">
          {[
            { val: `${metrics.peak_flow_l} L`,     lbl: "Peak flow" },
            { val: `${metrics.total_outflow_l} L`,  lbl: "Total outflow" },
            { val: `${metrics.rainfall_input_l} L`, lbl: "Rainfall input" },
          ].map(m => (
            <div key={m.lbl} className="ea-metric">
              <span className="ea-metric-val">{m.val}</span>
              <span className="ea-metric-lbl">{m.lbl}</span>
            </div>
          ))}
        </div>
      )}

      <div className="ea-section-label">Flow Hydrograph</div>
      <ResponsiveContainer width="100%" height={260}>
        <ComposedChart data={timeseries} margin={{ top: 4, right: 48, left: 0, bottom: 0 }}>
          <CartesianGrid strokeDasharray="3 3" stroke="#f0f0f0" />
          <XAxis
            dataKey="time"
            tickFormatter={t => fmtDatetime(t)}
            interval="preserveStartEnd"
            tick={{ fontSize: 10 }}
          />
          <YAxis
            yAxisId="flow"
            domain={[0, maxFlow * 1.2]}
            unit=" L"
            tick={{ fontSize: 10 }}
            label={{ value: "Flow (L)", angle: -90, position: "insideLeft", style: { fontSize: 10 } }}
          />
          <YAxis
            yAxisId="rain"
            orientation="right"
            reversed
            domain={[0, maxRain * 1.5]}
            unit=" mm"
            tick={{ fontSize: 10 }}
            label={{ value: "Rain (mm)", angle: 90, position: "insideRight", style: { fontSize: 10 } }}
          />
          <Tooltip
            labelFormatter={t => fmtDatetime(t)}
            formatter={(v, name) => [
              v !== null && v !== undefined ? v.toFixed(1) : "—",
              name === "rain" ? "Rain (mm)" : "Flow (L per 30 min)",
            ]}
          />
          <Bar yAxisId="flow" dataKey="flow_l" fill="#0d9488" radius={[2, 2, 0, 0]} name="flow" opacity={0.85} />
          <Bar yAxisId="rain" dataKey="rain" fill="#bfdbfe" opacity={0.7} name="rain" />
        </ComposedChart>
      </ResponsiveContainer>

      <div className="ea-sm-legend">
        <span className="ea-sm-key">
          <span className="ea-sm-dot" style={{ background: "#0d9488" }} /> Flow (L per 30 min)
        </span>
        <span className="ea-sm-key">
          <span className="ea-sm-dot" style={{ background: "#bfdbfe" }} /> Rain (mm, inverted)
        </span>
      </div>
    </div>
  );
}

// ── EC (water quality) panel ───────────────────────────────────────────────────

function ECPanel({ eventId, siteId, ecTrend }) {
  const [data, setData]       = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    if (!eventId) return;
    setLoading(true);
    setData(null);
    fetch(`${API}/hydro/events/${eventId}/ec?site_id=${siteId}`)
      .then(r => r.json())
      .then(d => { setData(d); setLoading(false); })
      .catch(() => setLoading(false));
  }, [eventId, siteId]);

  if (loading) return <div className="ea-loading">Loading EC data…</div>;

  const hasData = data && data.timeseries &&
    data.timeseries.some(r => SM_DEPTHS.some(d => r[`ec_${d}`] !== null));

  if (!hasData) {
    return (
      <div className="ea-empty">
        No electrical conductivity data available for this event.
        EC data is from Sentek TriSCAN sensors (data coverage: Dec 2022 – Jan 2025).
      </div>
    );
  }

  const { timeseries, first_flush } = data;

  return (
    <div>
      <div className="ea-section-label">Electrical Conductivity — Event Window (VIC)</div>
      <ResponsiveContainer width="100%" height={240}>
        <ComposedChart data={timeseries} margin={{ top: 4, right: 16, left: 0, bottom: 0 }}>
          <CartesianGrid strokeDasharray="3 3" stroke="#f0f0f0" />
          <XAxis
            dataKey="time"
            tickFormatter={t => fmtDatetime(t)}
            interval="preserveStartEnd"
            tick={{ fontSize: 10 }}
          />
          <YAxis
            domain={["auto", "auto"]}
            unit=" VIC"
            tick={{ fontSize: 10 }}
            label={{ value: "EC (VIC)", angle: -90, position: "insideLeft", style: { fontSize: 10 } }}
          />
          <Tooltip
            labelFormatter={t => fmtDatetime(t)}
            formatter={(v, name) => [
              v !== null && v !== undefined ? v.toFixed(1) : "—",
              `${name.replace("ec_", "")} cm`,
            ]}
          />
          {SM_DEPTHS.map((d, i) => (
            <Line
              key={d}
              dataKey={`ec_${d}`}
              stroke={EC_COLORS[i]}
              dot={false}
              strokeWidth={2}
              connectNulls
              name={`ec_${d}`}
            />
          ))}
        </ComposedChart>
      </ResponsiveContainer>

      <div className="ea-sm-legend">
        {SM_DEPTHS.map((d, i) => (
          <span key={d} className="ea-sm-key">
            <span className="ea-sm-dot" style={{ background: EC_COLORS[i] }} />
            {d} cm
          </span>
        ))}
      </div>

      {first_flush && Object.keys(first_flush).length > 0 && (
        <>
          <div className="ea-section-label" style={{ marginTop: 16 }}>First-Flush Metrics</div>
          <table className="ea-ff-table">
            <thead>
              <tr>
                <th>Depth</th>
                <th>Pre-event (VIC)</th>
                <th>Peak flush (VIC)</th>
                <th>Post-event (VIC)</th>
                <th>Change</th>
              </tr>
            </thead>
            <tbody>
              {SM_DEPTHS.map((d, i) => {
                const ff = first_flush[String(d)];
                if (!ff || (ff.pre_vic === null && ff.flush_vic === null)) return null;
                const changePct = ff.pre_vic && ff.flush_vic
                  ? (((ff.flush_vic - ff.pre_vic) / ff.pre_vic) * 100).toFixed(1)
                  : null;
                return (
                  <tr key={d}>
                    <td>
                      <span className="ea-sm-dot" style={{ background: EC_COLORS[i], display: "inline-block", marginRight: 4 }} />
                      {d} cm
                    </td>
                    <td>{ff.pre_vic ?? "—"}</td>
                    <td>{ff.flush_vic ?? "—"}</td>
                    <td>{ff.post_vic ?? "—"}</td>
                    <td style={{ color: changePct > 0 ? "#ef4444" : "#16a34a", fontWeight: 600 }}>
                      {changePct !== null ? `${changePct > 0 ? "+" : ""}${changePct}%` : "—"}
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </>
      )}

      {ecTrend && ecTrend.length > 0 && (
        <>
          <div className="ea-section-label" style={{ marginTop: 20 }}>Long-Term EC Trend (monthly avg)</div>
          <ResponsiveContainer width="100%" height={200}>
            <ComposedChart data={ecTrend} margin={{ top: 4, right: 16, left: 0, bottom: 0 }}>
              <CartesianGrid strokeDasharray="3 3" stroke="#f0f0f0" />
              <XAxis dataKey="month" tick={{ fontSize: 10 }} interval="preserveStartEnd" />
              <YAxis
                domain={["auto", "auto"]}
                unit=" VIC"
                tick={{ fontSize: 10 }}
              />
              <Tooltip
                formatter={(v, name) => [
                  v !== null ? v.toFixed(1) : "—",
                  `${name.replace("ec_", "")} cm`,
                ]}
              />
              {SM_DEPTHS.map((d, i) => (
                <Line
                  key={d}
                  dataKey={`ec_${d}`}
                  stroke={EC_COLORS[i]}
                  dot={false}
                  strokeWidth={1.5}
                  connectNulls
                  name={`ec_${d}`}
                />
              ))}
            </ComposedChart>
          </ResponsiveContainer>
        </>
      )}
    </div>
  );
}

// ── Drainage rate panel ────────────────────────────────────────────────────────

function computeDrainage(timeseries) {
  const results = {};
  SM_DEPTHS.forEach(d => {
    const vals = timeseries
      .map(r => r[`sm_${d}`])
      .filter((v, i, a) => {
        // keep track of original index for time alignment
        a[i] = { sm: v, i };
        return v !== null && v !== undefined;
      });
    // rebuild with original index
    const indexed = timeseries
      .map((r, i) => ({ sm: r[`sm_${d}`], i }))
      .filter(r => r.sm !== null && r.sm !== undefined);

    if (indexed.length < 5) return;

    const baseline = indexed.slice(0, 3).reduce((s, r) => s + r.sm, 0) / 3;
    const peakIdx  = indexed.reduce((best, r, i) => r.sm > indexed[best].sm ? i : best, 0);
    const peak     = indexed[peakIdx];
    const gain     = peak.sm - baseline;
    if (gain < 0.5) return;

    const postPeak = indexed.slice(peakIdx);
    if (postPeak.length < 3) return;

    // Linear regression slope (pp per 30-min step → pp/hr × 2)
    const n     = postPeak.length;
    const xs    = postPeak.map((_, i) => i);
    const ys    = postPeak.map(r => r.sm);
    const xMean = xs.reduce((s, x) => s + x, 0) / n;
    const yMean = ys.reduce((s, y) => s + y, 0) / n;
    const denom = xs.reduce((s, x) => s + (x - xMean) ** 2, 0);
    const slope = denom > 0
      ? xs.reduce((s, x, i) => s + (x - xMean) * (ys[i] - yMean), 0) / denom
      : 0;
    const ratePpHr = Math.round(Math.abs(slope) * 2 * 10) / 10; // steps→hours

    // Half-life: steps until SM ≤ baseline + gain/2
    const halfTarget = baseline + gain * 0.5;
    const halfStep   = postPeak.findIndex(r => r.sm <= halfTarget);
    const halfLifeH  = halfStep > 0 ? Math.round(halfStep * 0.5 * 10) / 10 : null;

    results[d] = {
      baseline:   Math.round(baseline * 10) / 10,
      peak_sm:    Math.round(peak.sm * 10) / 10,
      gain:       Math.round(gain * 10) / 10,
      rate_pp_hr: ratePpHr,
      half_life_h: halfLifeH,
      postPeak:   postPeak.map((r, i) => ({ hours: i * 0.5, sm: Math.round(r.sm * 10) / 10 })),
    };
  });
  return results;
}

function DrainagePanel({ timeseries }) {
  if (!timeseries || timeseries.length === 0) {
    return <div className="ea-empty">No soil moisture data available for this event.</div>;
  }

  const drainage = computeDrainage(timeseries);
  const depths   = SM_DEPTHS.filter(d => drainage[d]);

  if (depths.length === 0) {
    return <div className="ea-empty">Insufficient moisture response to compute drainage rates (event gain &lt;0.5pp at all depths).</div>;
  }

  // Build merged post-peak chart dataset aligned to hours-since-peak per depth
  const maxSteps = Math.max(...depths.map(d => drainage[d].postPeak.length));
  const chartData = Array.from({ length: maxSteps }, (_, i) => {
    const row = { hours: i * 0.5 };
    depths.forEach(d => {
      const pp = drainage[d].postPeak[i];
      row[`sm_${d}`] = pp ? pp.sm : null;
    });
    return row;
  });

  return (
    <div>
      <div className="ea-section-label">Post-Peak SM Decline (hours since each depth's peak)</div>
      <ResponsiveContainer width="100%" height={240}>
        <ComposedChart data={chartData} margin={{ top: 4, right: 16, left: 0, bottom: 0 }}>
          <CartesianGrid strokeDasharray="3 3" stroke="#f0f0f0" />
          <XAxis
            dataKey="hours"
            unit=" h"
            tick={{ fontSize: 10 }}
            label={{ value: "Hours since peak", position: "insideBottom", offset: -2, style: { fontSize: 10 } }}
          />
          <YAxis
            domain={["auto", "auto"]}
            unit="%"
            tick={{ fontSize: 10 }}
            label={{ value: "SM %", angle: -90, position: "insideLeft", style: { fontSize: 10 } }}
          />
          <Tooltip
            formatter={(v, name) => [
              v !== null && v !== undefined ? `${v}%` : "—",
              `${name.replace("sm_", "")} cm`,
            ]}
            labelFormatter={v => `${v} h after peak`}
          />
          {depths.map((d, i) => (
            <Line
              key={d}
              dataKey={`sm_${d}`}
              stroke={SM_COLORS[SM_DEPTHS.indexOf(d)]}
              dot={false}
              strokeWidth={2}
              connectNulls={false}
              name={`sm_${d}`}
            />
          ))}
        </ComposedChart>
      </ResponsiveContainer>

      <div className="ea-sm-legend" style={{ marginBottom: 12 }}>
        {depths.map(d => (
          <span key={d} className="ea-sm-key">
            <span className="ea-sm-dot" style={{ background: SM_COLORS[SM_DEPTHS.indexOf(d)] }} />
            {d} cm
          </span>
        ))}
      </div>

      <div className="ea-section-label">Drainage Metrics</div>
      <table className="ea-ff-table">
        <thead>
          <tr>
            <th>Depth</th>
            <th>Baseline SM</th>
            <th>Peak SM</th>
            <th>Event gain</th>
            <th>Drainage rate</th>
            <th>Half-life</th>
          </tr>
        </thead>
        <tbody>
          {depths.map((d, i) => {
            const r = drainage[d];
            return (
              <tr key={d}>
                <td>
                  <span className="ea-sm-dot" style={{ background: SM_COLORS[SM_DEPTHS.indexOf(d)], display: "inline-block", marginRight: 4 }} />
                  {d} cm
                </td>
                <td>{r.baseline}%</td>
                <td>{r.peak_sm}%</td>
                <td style={{ color: "#2563eb", fontWeight: 600 }}>+{r.gain} pp</td>
                <td>{r.rate_pp_hr > 0 ? `${r.rate_pp_hr} pp/hr` : "—"}</td>
                <td>{r.half_life_h !== null ? `${r.half_life_h} h` : "> window"}</td>
              </tr>
            );
          })}
        </tbody>
      </table>
    </div>
  );
}

// ── Long-term SM trend ─────────────────────────────────────────────────────────

function SMTrendSection({ trend, siteId }) {
  if (!trend || trend.length === 0) return null;

  const data = trend.map(r => ({ ...r, label: fmtMonth(r.month) }));

  function handleExport() {
    const depths = SM_DEPTHS.map(d => `sm_${d}`);
    const header = ["month", ...depths].join(",");
    const rows   = trend.map(r => [r.month, ...depths.map(k => r[k] ?? "")].join(","));
    downloadCSV(`sm_trend_site${siteId}.csv`, [header, ...rows]);
  }

  return (
    <div className="ea-card">
      <div className="ea-card-header">
        <div className="ea-card-title">Long-Term Soil Moisture Trend (monthly avg)</div>
        <button className="ea-btn-sm" onClick={handleExport}>CSV</button>
      </div>
      <ResponsiveContainer width="100%" height={220}>
        <ComposedChart data={data} margin={{ top: 4, right: 16, left: 0, bottom: 0 }}>
          <CartesianGrid strokeDasharray="3 3" stroke="#f0f0f0" />
          <XAxis dataKey="label" tick={{ fontSize: 10 }} interval="preserveStartEnd" />
          <YAxis
            domain={["auto", "auto"]}
            unit="%"
            tick={{ fontSize: 10 }}
            label={{ value: "SM %", angle: -90, position: "insideLeft", style: { fontSize: 10 } }}
          />
          <Tooltip
            formatter={(v, name) => [
              v !== null && v !== undefined ? `${v}%` : "—",
              `${name.replace("sm_", "")} cm`,
            ]}
          />
          {SM_DEPTHS.map((d, i) => (
            <Line
              key={d}
              dataKey={`sm_${d}`}
              stroke={SM_COLORS[i]}
              dot={false}
              strokeWidth={2}
              connectNulls
              name={`sm_${d}`}
            />
          ))}
        </ComposedChart>
      </ResponsiveContainer>
      <div className="ea-sm-legend">
        {SM_DEPTHS.map((d, i) => (
          <span key={d} className="ea-sm-key">
            <span className="ea-sm-dot" style={{ background: SM_COLORS[i] }} />
            {d} cm
          </span>
        ))}
      </div>
    </div>
  );
}

// ── Soil temperature trend ─────────────────────────────────────────────────────

function TempTrendSection({ trend }) {
  if (!trend || trend.length === 0) return null;

  const data = trend.map(r => ({ ...r, label: fmtMonth(r.month) }));

  return (
    <div className="ea-card">
      <div className="ea-card-title">Long-Term Soil Temperature Trend (monthly avg)</div>
      <ResponsiveContainer width="100%" height={220}>
        <ComposedChart data={data} margin={{ top: 4, right: 16, left: 0, bottom: 0 }}>
          <CartesianGrid strokeDasharray="3 3" stroke="#f0f0f0" />
          <XAxis dataKey="label" tick={{ fontSize: 10 }} interval="preserveStartEnd" />
          <YAxis
            domain={["auto", "auto"]}
            unit="°C"
            tick={{ fontSize: 10 }}
            label={{ value: "°C", angle: -90, position: "insideLeft", style: { fontSize: 10 } }}
          />
          <Tooltip
            formatter={(v, name) => [
              v !== null && v !== undefined ? `${v}°C` : "—",
              `${name.replace("temp_", "")} cm`,
            ]}
          />
          {SM_DEPTHS.map((d, i) => (
            <Line
              key={d}
              dataKey={`temp_${d}`}
              stroke={TEMP_COLORS[i]}
              dot={false}
              strokeWidth={2}
              connectNulls
              name={`temp_${d}`}
            />
          ))}
        </ComposedChart>
      </ResponsiveContainer>
      <div className="ea-sm-legend">
        {SM_DEPTHS.map((d, i) => (
          <span key={d} className="ea-sm-key">
            <span className="ea-sm-dot" style={{ background: TEMP_COLORS[i] }} />
            {d} cm
          </span>
        ))}
      </div>
    </div>
  );
}

// ── Inter-site comparison ──────────────────────────────────────────────────────

function CompareColumn({ label, detail, loading }) {
  if (loading) return (
    <div className="ea-compare-col">
      <div className="ea-compare-site-label">{label}</div>
      <div className="ea-loading">Loading {label}…</div>
    </div>
  );
  if (!detail) return (
    <div className="ea-compare-col">
      <div className="ea-compare-site-label">{label}</div>
      <div className="ea-empty">No matched event detected on {label} for this date.</div>
    </div>
  );

  const { balance, timeseries } = detail;
  const effColor = balance
    ? balance.efficiency_pct >= 90 ? "#16a34a"
      : balance.efficiency_pct >= 75 ? "#f59e0b" : "#ef4444"
    : "#6b7280";

  return (
    <div className="ea-compare-col">
      <div className="ea-compare-site-label">{label}</div>

      {balance && (
        <div className="ea-compare-eff" style={{ color: effColor }}>
          {balance.efficiency_pct}% efficient
        </div>
      )}

      {balance && (
        <div className="ea-metrics-row ea-metrics-wrap">
          {[
            { val: `${balance.sm_start_pct}%`,      lbl: "SM start" },
            { val: `${balance.sm_end_pct}%`,        lbl: "SM end" },
            { val: `${balance.storage_change_l} L`, lbl: "ΔStorage" },
            { val: `${balance.infiltration_l} L`,   lbl: "Infiltration" },
            { val: `${balance.runoff_l} L`,         lbl: "Runoff" },
          ].map(m => (
            <div key={m.lbl} className="ea-metric">
              <span className="ea-metric-val">{m.val}</span>
              <span className="ea-metric-lbl">{m.lbl}</span>
            </div>
          ))}
        </div>
      )}

      {timeseries && timeseries.length > 0 && (
        <>
          <div className="ea-section-label">Soil Moisture</div>
          <SMHeatmap timeseries={timeseries} />
        </>
      )}

      {balance && <WaterBalance balance={balance} />}
    </div>
  );
}

function ComparePanel({ currentDetail, currentSiteId }) {
  const currentSite = SITES.find(s => s.site_id === currentSiteId);
  const otherSite   = SITES.find(s => s.site_id !== currentSiteId);
  const [otherDetail, setOtherDetail] = useState(null);
  const [loading, setLoading]         = useState(true);

  useEffect(() => {
    if (!currentDetail) return;
    setLoading(true);
    setOtherDetail(null);
    const eventId = currentDetail.event.id;
    fetch(`${API}/hydro/events/${eventId}/match?from_site=${currentSiteId}&to_site=${otherSite.site_id}`)
      .then(r => r.json())
      .then(({ matched_id }) => {
        if (!matched_id) { setLoading(false); return; }
        return fetch(`${API}/hydro/events/${matched_id}?site_id=${otherSite.site_id}`)
          .then(r => r.json())
          .then(d => { setOtherDetail(d); setLoading(false); });
      })
      .catch(() => setLoading(false));
  }, [currentDetail, currentSiteId]);

  return (
    <div className="ea-compare-grid">
      <CompareColumn label={currentSite.label} detail={currentDetail} loading={false} />
      <CompareColumn label={otherSite.label}   detail={otherDetail}   loading={loading} />
    </div>
  );
}

// ── Performance section ────────────────────────────────────────────────────────

const CLUSTER_COLORS = ["#0ea5e9", "#6366f1", "#f59e0b", "#ef4444"];

function PerformanceSection({ stats }) {
  if (!stats || (!stats.monthly.length && !stats.scatter.length)) return null;

  const monthly = stats.monthly.map(m => ({ ...m, label: fmtMonth(m.month) }));
  const clusters = stats.clusters || {};

  const scatterFiltered = stats.scatter
    .filter(r => r.efficiency_pct <= 100)
    .map(r => ({ ...r, rain_capped: Math.min(r.total_rainfall_mm, 150) }));

  const byCluster = {};
  scatterFiltered.forEach(r => {
    const k = r.cluster ?? 0;
    if (!byCluster[k]) byCluster[k] = [];
    byCluster[k].push(r);
  });
  const clusterKeys = Object.keys(byCluster).map(Number).sort();
  const hasMultipleClusters = clusterKeys.length > 1;

  return (
    <div className="ea-card ea-perf-card">
      <div className="ea-card-title">System Performance — All Events</div>
      <div className="ea-perf-row">

        <div className="ea-perf-chart">
          <div className="ea-section-label" style={{ marginTop: 0 }}>Monthly Summary</div>
          <ResponsiveContainer width="100%" height={200}>
            <ComposedChart data={monthly} margin={{ top: 4, right: 48, left: 0, bottom: 0 }}>
              <CartesianGrid strokeDasharray="3 3" stroke="#f0f0f0" />
              <XAxis dataKey="label" tick={{ fontSize: 10 }} />
              <YAxis
                yAxisId="eff"
                domain={[0, 100]}
                unit="%"
                tick={{ fontSize: 10 }}
                label={{ value: "Efficiency %", angle: -90, position: "insideLeft", style: { fontSize: 10 } }}
              />
              <YAxis
                yAxisId="cnt"
                orientation="right"
                tick={{ fontSize: 10 }}
                label={{ value: "Events", angle: 90, position: "insideRight", style: { fontSize: 10 } }}
              />
              <Tooltip
                formatter={(v, name) => [
                  name === "avg_efficiency" ? `${v}%` : Math.round(v),
                  name === "avg_efficiency" ? "Avg efficiency" : "Events",
                ]}
              />
              <Bar yAxisId="cnt" dataKey="event_count" fill="#bfdbfe" radius={[2, 2, 0, 0]} name="event_count" />
              <Line yAxisId="eff" dataKey="avg_efficiency" stroke="#16a34a" dot={{ r: 3 }} strokeWidth={2} name="avg_efficiency" />
            </ComposedChart>
          </ResponsiveContainer>
          <div className="ea-sm-legend">
            <span className="ea-sm-key"><span className="ea-sm-dot" style={{ background: "#bfdbfe" }} /> Events per month</span>
            <span className="ea-sm-key"><span className="ea-sm-dot" style={{ background: "#16a34a" }} /> Avg efficiency (%)</span>
          </div>
        </div>

        <div className="ea-perf-chart">
          <div className="ea-section-label" style={{ marginTop: 0 }}>Antecedent Dry Period vs Efficiency</div>
          <ResponsiveContainer width="100%" height={200}>
            <ScatterChart margin={{ top: 4, right: 16, left: 0, bottom: 20 }}>
              <CartesianGrid strokeDasharray="3 3" stroke="#f0f0f0" />
              <XAxis
                dataKey="antecedent_dry_days"
                name="ADP"
                type="number"
                unit=" d"
                tick={{ fontSize: 10 }}
                label={{ value: "Antecedent dry (days)", position: "insideBottom", offset: -12, style: { fontSize: 10 } }}
              />
              <YAxis
                dataKey="efficiency_pct"
                name="Efficiency"
                domain={[70, 100]}
                unit="%"
                tick={{ fontSize: 10 }}
              />
              <ZAxis dataKey="rain_capped" range={[30, 260]} name="Rainfall (mm)" />
              <Tooltip
                cursor={{ strokeDasharray: "3 3" }}
                content={({ payload }) => {
                  if (!payload || !payload.length) return null;
                  const d = payload[0].payload;
                  return (
                    <div className="ea-scatter-tip">
                      <strong>{d.label}</strong>
                      {hasMultipleClusters && (
                        <div style={{ color: CLUSTER_COLORS[d.cluster ?? 0], fontWeight: 600 }}>
                          Cluster {(d.cluster ?? 0) + 1}
                        </div>
                      )}
                      <div>ADP: {d.antecedent_dry_days} d</div>
                      <div>Duration: {d.duration_hours} h</div>
                      <div>Efficiency: {Math.min(d.efficiency_pct, 100)}%</div>
                      <div>Rainfall: {d.total_rainfall_mm} mm</div>
                    </div>
                  );
                }}
              />
              {clusterKeys.map(k => (
                <Scatter
                  key={k}
                  name={`Cluster ${k + 1}`}
                  data={byCluster[k]}
                  fill={CLUSTER_COLORS[k]}
                  opacity={0.72}
                />
              ))}
            </ScatterChart>
          </ResponsiveContainer>
          <div className="ea-scatter-note">
            Bubble size = rainfall depth (capped at 150 mm) ·{" "}
            {hasMultipleClusters ? "colour = k-means cluster (ADP + duration)" : "hover for details"}
          </div>

          {hasMultipleClusters && (
            <div className="ea-cluster-legend">
              {clusterKeys.map(k => {
                const c = clusters[k] || {};
                return (
                  <div key={k} className="ea-cluster-item">
                    <span className="ea-cluster-dot" style={{ background: CLUSTER_COLORS[k] }} />
                    <div className="ea-cluster-info">
                      <span className="ea-cluster-name">Cluster {k + 1}</span>
                      <span className="ea-cluster-stats">
                        {c.count} events · ADP {c.avg_adp}d · {c.avg_duration}h · {c.avg_efficiency}% eff
                      </span>
                    </div>
                  </div>
                );
              })}
            </div>
          )}
        </div>

      </div>
    </div>
  );
}

// ── Long-term EC trend ─────────────────────────────────────────────────────────

function ECTrendSection({ trend, siteId }) {
  if (!trend || trend.length === 0) return null;

  const data = trend.map(r => ({ ...r, label: fmtMonth(r.month) }));

  function handleExport() {
    const depths = SM_DEPTHS.map(d => `ec_${d}`);
    const header = ["month", ...depths].join(",");
    const rows   = trend.map(r => [r.month, ...depths.map(k => r[k] ?? "")].join(","));
    downloadCSV(`ec_trend_site${siteId}.csv`, [header, ...rows]);
  }

  return (
    <div className="ea-card">
      <div className="ea-card-header">
        <div className="ea-card-title">Long-Term EC Trend (monthly avg)</div>
        <button className="ea-btn-sm" onClick={handleExport}>CSV</button>
      </div>
      <ResponsiveContainer width="100%" height={220}>
        <ComposedChart data={data} margin={{ top: 4, right: 16, left: 0, bottom: 0 }}>
          <CartesianGrid strokeDasharray="3 3" stroke="#f0f0f0" />
          <XAxis dataKey="label" tick={{ fontSize: 10 }} interval="preserveStartEnd" />
          <YAxis
            domain={["auto", "auto"]}
            tick={{ fontSize: 10 }}
            label={{ value: "EC (VIC)", angle: -90, position: "insideLeft", style: { fontSize: 10 } }}
          />
          <Tooltip
            formatter={(v, name) => [
              v !== null && v !== undefined ? v : "—",
              `${name.replace("ec_", "")} cm`,
            ]}
          />
          {SM_DEPTHS.map((d, i) => (
            <Line
              key={d}
              dataKey={`ec_${d}`}
              stroke={EC_COLORS[i]}
              dot={false}
              strokeWidth={2}
              connectNulls
              name={`ec_${d}`}
            />
          ))}
        </ComposedChart>
      </ResponsiveContainer>
      <div className="ea-sm-legend">
        {SM_DEPTHS.map((d, i) => (
          <span key={d} className="ea-sm-key">
            <span className="ea-sm-dot" style={{ background: EC_COLORS[i] }} />
            {d} cm
          </span>
        ))}
      </div>
    </div>
  );
}

// ── Seasonal performance breakdown ─────────────────────────────────────────────

const SEASON_COLORS = { DJF: "#60a5fa", MAM: "#4ade80", JJA: "#facc15", SON: "#fb923c" };
const SEASON_LABELS = { DJF: "Winter (DJF)", MAM: "Spring (MAM)", JJA: "Summer (JJA)", SON: "Autumn (SON)" };

function SeasonalSection({ data }) {
  if (!data || data.length === 0) return null;

  const chartData = data.map(d => ({
    ...d,
    fill: SEASON_COLORS[d.season],
    label: SEASON_LABELS[d.season] || d.season,
  }));

  return (
    <div className="ea-card">
      <div className="ea-card-header">
        <div className="ea-card-title">Seasonal Performance Breakdown</div>
      </div>
      <ResponsiveContainer width="100%" height={220}>
        <ComposedChart data={chartData} margin={{ top: 8, right: 24, left: 0, bottom: 0 }}>
          <CartesianGrid strokeDasharray="3 3" stroke="#f0f0f0" />
          <XAxis dataKey="label" tick={{ fontSize: 11 }} />
          <YAxis
            yAxisId="count"
            orientation="left"
            tick={{ fontSize: 10 }}
            label={{ value: "Events", angle: -90, position: "insideLeft", style: { fontSize: 10 } }}
          />
          <YAxis
            yAxisId="eff"
            orientation="right"
            domain={[0, 100]}
            tick={{ fontSize: 10 }}
            label={{ value: "Efficiency %", angle: 90, position: "insideRight", style: { fontSize: 10 } }}
          />
          <Tooltip
            formatter={(v, name) =>
              name === "event_count"
                ? [v, "Events"]
                : name === "mean_efficiency_pct"
                ? [`${v}%`, "Mean efficiency"]
                : [`${v}%`, "Peak efficiency"]
            }
          />
          <Bar yAxisId="count" dataKey="event_count" radius={[4, 4, 0, 0]}>
            {chartData.map((d, i) => (
              <Cell key={i} fill={d.fill} />
            ))}
          </Bar>
          <Line yAxisId="eff" dataKey="mean_efficiency_pct" stroke="#6366f1" strokeWidth={2} dot={{ r: 4 }} name="mean_efficiency_pct" />
          <Line yAxisId="eff" dataKey="peak_efficiency_pct" stroke="#a855f7" strokeWidth={2} strokeDasharray="4 3" dot={{ r: 3 }} name="peak_efficiency_pct" />
        </ComposedChart>
      </ResponsiveContainer>
      <div className="ea-sm-legend">
        <span className="ea-sm-key"><span className="ea-sm-dot" style={{ background: "#6366f1" }} />Mean eff.</span>
        <span className="ea-sm-key"><span className="ea-sm-dot" style={{ background: "#a855f7", opacity: 0.6 }} />Peak eff.</span>
        {chartData.map(d => (
          <span key={d.season} className="ea-sm-key">
            <span className="ea-sm-dot" style={{ background: d.fill }} />{d.season} ({d.event_count})
          </span>
        ))}
      </div>
    </div>
  );
}

// ── EC–SM dilution scatter ─────────────────────────────────────────────────────

const QUADRANT_STYLE = { fontSize: 10, fill: "#d1d5db", fontStyle: "italic" };

function ECSmScatterSection({ data }) {
  if (!data || data.length === 0) return null;

  return (
    <div className="ea-card">
      <div className="ea-card-header">
        <div className="ea-card-title">EC–SM Dilution Scatter</div>
      </div>
      <ResponsiveContainer width="100%" height={260}>
        <ScatterChart margin={{ top: 16, right: 24, left: 0, bottom: 24 }}>
          <CartesianGrid strokeDasharray="3 3" stroke="#f0f0f0" />
          <ReferenceLine x={0} stroke="#e5e7eb" strokeDasharray="4 4" />
          <ReferenceLine y={0} stroke="#e5e7eb" strokeDasharray="4 4" />
          <XAxis
            dataKey="delta_sm"
            name="ΔSM"
            type="number"
            unit="%"
            tick={{ fontSize: 10 }}
            label={{ value: "ΔSoil Moisture (%)", position: "insideBottom", offset: -14, style: { fontSize: 10 } }}
          />
          <YAxis
            dataKey="delta_ec_drop"
            name="ΔEC drop"
            type="number"
            tick={{ fontSize: 10 }}
            label={{ value: "EC drop (start − end)", angle: -90, position: "insideLeft", style: { fontSize: 10 } }}
          />
          <Tooltip
            cursor={{ strokeDasharray: "3 3" }}
            content={({ payload }) => {
              if (!payload || !payload.length) return null;
              const d = payload[0].payload;
              const diluting = d.delta_ec_drop > 0;
              return (
                <div className="ea-scatter-tip">
                  <strong>{d.label}</strong>
                  <div>ΔSM: {d.delta_sm > 0 ? "+" : ""}{d.delta_sm}%</div>
                  <div>EC start: {d.ec_start}</div>
                  <div>EC end: {d.ec_end}</div>
                  <div style={{ color: diluting ? "#16a34a" : "#dc2626", fontWeight: 600 }}>
                    {diluting ? `Dilution: −${d.delta_ec_drop}` : `Concentration: +${Math.abs(d.delta_ec_drop)}`}
                  </div>
                </div>
              );
            }}
          />
          <Scatter data={data} fill="#0077ff" opacity={0.65} />
        </ScatterChart>
      </ResponsiveContainer>
      <div className="ea-scatter-note">
        Right of x=0: SM increased · Above y=0: EC dropped (dilution by fresh rainwater) ·
        Below y=0: EC rose (salt concentration / evaporative enrichment)
      </div>
    </div>
  );
}

// ── Event detail (tabbed) ──────────────────────────────────────────────────────

function EventDetail({ detail, loading, siteId, ecTrend }) {
  const [activeTab, setActiveTab] = useState("overview");

  useEffect(() => { setActiveTab("overview"); }, [detail]);

  if (loading) return <div className="ea-card ea-loading-card">Loading event detail…</div>;
  if (!detail)  return null;

  const { event, balance, timeseries } = detail;

  const metricItems = [
    { val: `${event.total_rainfall_mm} mm`,     lbl: "Total rainfall" },
    { val: `${event.duration_hours} h`,          lbl: "Duration" },
    { val: `${event.peak_intensity_mmhr} mm/hr`, lbl: "Peak intensity" },
    { val: `${event.antecedent_dry_days} d`,     lbl: "Antecedent dry" },
    ...(balance ? [
      { val: `${balance.efficiency_pct}%`, lbl: "Efficiency" },
      { val: `${balance.sm_start_pct}%`,   lbl: "SM at start" },
      { val: `${balance.sm_end_pct}%`,     lbl: "SM at end" },
    ] : []),
  ];

  const TABS = [
    { key: "overview",  label: "Overview" },
    { key: "soiltemp",  label: "Soil Temp" },
    { key: "drainage",  label: "Drainage Rate" },
    { key: "flow",      label: "Flow Hydrograph" },
    { key: "ec",        label: "Water Quality" },
    { key: "compare",   label: "Compare Sites" },
  ];

  return (
    <div className="ea-card ea-detail-card">
      <div className="ea-card-title">
        {fmtDatetime(event.event_start)} — Event Detail
      </div>

      <div className="ea-metrics-row ea-metrics-wrap">
        {metricItems.map(m => (
          <div key={m.lbl} className="ea-metric">
            <span className="ea-metric-val">{m.val}</span>
            <span className="ea-metric-lbl">{m.lbl}</span>
          </div>
        ))}
      </div>

      <div className="ea-detail-tabs">
        {TABS.map(t => (
          <button
            key={t.key}
            className={`ea-detail-tab${activeTab === t.key ? " active" : ""}`}
            onClick={() => setActiveTab(t.key)}
          >
            {t.label}
          </button>
        ))}
      </div>

      {activeTab === "overview" && (
        <>
          {timeseries && timeseries.length > 0 && (
            <>
              <div className="ea-section-label">Soil Moisture Response</div>
              <SMHeatmap timeseries={timeseries} />
            </>
          )}
          {balance && <WaterBalance balance={balance} />}
        </>
      )}

      {activeTab === "soiltemp" && (
        <>
          <div className="ea-section-label">Soil Temperature Profile</div>
          <TempHeatmap timeseries={timeseries} />
        </>
      )}

      {activeTab === "drainage" && (
        <DrainagePanel timeseries={timeseries} />
      )}

      {activeTab === "flow" && (
        <FlowPanel eventId={event.id} siteId={siteId} />
      )}

      {activeTab === "ec" && (
        <ECPanel eventId={event.id} siteId={siteId} ecTrend={ecTrend} />
      )}

      {activeTab === "compare" && (
        <ComparePanel currentDetail={detail} currentSiteId={siteId} />
      )}
    </div>
  );
}

// ── Main page ──────────────────────────────────────────────────────────────────

export default function EventAnalysis() {
  const [siteId, setSiteId]             = useState(5);
  const [events, setEvents]             = useState([]);
  const [selectedId, setSelectedId]     = useState(null);
  const [detail, setDetail]             = useState(null);
  const [loadingDetail, setLoadingDetail] = useState(false);
  const [rec, setRec]                   = useState(null);
  const [forecast, setForecast]         = useState([]);
  const [detecting, setDetecting]       = useState(false);
  const [ecTrend, setEcTrend]           = useState([]);
  const [smTrend, setSmTrend]           = useState([]);
  const [tempTrend, setTempTrend]       = useState([]);
  const [perf, setPerf]                 = useState(null);
  const [cumulative, setCumulative]     = useState(null);
  const [ecSmData, setEcSmData]         = useState([]);
  const [seasonal, setSeasonal]         = useState([]);

  const fetchEvents = useCallback(async () => {
    try {
      const res = await fetch(`${API}/hydro/events?site_id=${siteId}&limit=60`);
      setEvents(await res.json());
    } catch { setEvents([]); }
  }, [siteId]);

  const fetchRec = useCallback(async () => {
    try {
      const res = await fetch(`${API}/hydro/recommend?site_id=${siteId}`);
      setRec(await res.json());
    } catch { setRec(null); }
  }, [siteId]);

  const fetchForecast = useCallback(async () => {
    try {
      const res = await fetch(`${API}/hydro/forecast`);
      setForecast(await res.json());
    } catch { setForecast([]); }
  }, []);

  const fetchEcTrend = useCallback(async () => {
    try {
      const res = await fetch(`${API}/hydro/ec-trend?site_id=${siteId}`);
      setEcTrend(await res.json());
    } catch { setEcTrend([]); }
  }, [siteId]);

  const fetchSmTrend = useCallback(async () => {
    try {
      const res = await fetch(`${API}/hydro/sm-trend?site_id=${siteId}`);
      setSmTrend(await res.json());
    } catch { setSmTrend([]); }
  }, [siteId]);

  const fetchTempTrend = useCallback(async () => {
    try {
      const res = await fetch(`${API}/hydro/temp-trend?site_id=${siteId}`);
      setTempTrend(await res.json());
    } catch { setTempTrend([]); }
  }, [siteId]);

  const fetchPerf = useCallback(async () => {
    try {
      const res = await fetch(`${API}/hydro/performance?site_id=${siteId}`);
      setPerf(await res.json());
    } catch { setPerf(null); }
  }, [siteId]);

  const fetchCumulative = useCallback(async () => {
    try {
      const res = await fetch(`${API}/hydro/cumulative?site_id=${siteId}`);
      setCumulative(await res.json());
    } catch { setCumulative(null); }
  }, [siteId]);

  const fetchEcSm = useCallback(async () => {
    try {
      const res = await fetch(`${API}/hydro/ec-sm-scatter?site_id=${siteId}`);
      setEcSmData(await res.json());
    } catch { setEcSmData([]); }
  }, [siteId]);

  const fetchSeasonal = useCallback(async () => {
    try {
      const res = await fetch(`${API}/hydro/seasonal?site_id=${siteId}`);
      setSeasonal(await res.json());
    } catch { setSeasonal([]); }
  }, [siteId]);

  useEffect(() => {
    setEvents([]);
    setSelectedId(null);
    setDetail(null);
    setRec(null);
    setEcTrend([]);
    setSmTrend([]);
    setTempTrend([]);
    setPerf(null);
    setCumulative(null);
    setEcSmData([]);
    setSeasonal([]);
    fetchEvents();
    fetchRec();
    fetchForecast();
    fetchEcTrend();
    fetchSmTrend();
    fetchTempTrend();
    fetchPerf();
    fetchCumulative();
    fetchEcSm();
    fetchSeasonal();
  }, [siteId, fetchEvents, fetchRec, fetchForecast, fetchEcTrend, fetchSmTrend, fetchTempTrend, fetchPerf, fetchCumulative, fetchEcSm, fetchSeasonal]);

  async function handleDetect() {
    setDetecting(true);
    try {
      await fetch(`${API}/hydro/detect?site_id=${siteId}&days=730`, { method: "POST" });
      await fetchEvents();
    } finally { setDetecting(false); }
  }

  async function handleSelectEvent(id) {
    setSelectedId(id);
    setLoadingDetail(true);
    try {
      const res = await fetch(`${API}/hydro/events/${id}?site_id=${siteId}`);
      setDetail(await res.json());
    } catch { setDetail(null); }
    setLoadingDetail(false);
  }

  return (
    <div className="ea-page">
      <div className="ea-page-header">
        <h2 className="ea-page-title">Event Analysis</h2>
        <div className="ea-tabs">
          {SITES.map(s => (
            <button
              key={s.site_id}
              className={`ea-tab${siteId === s.site_id ? " active" : ""}`}
              onClick={() => setSiteId(s.site_id)}
            >
              {s.label}
            </button>
          ))}
        </div>
      </div>

      <CumulativeCard stats={cumulative} />

      <div className="ea-top-row">
        <RecommendationCard rec={rec} />
        <ForecastStrip forecast={forecast} />
      </div>

      <PerformanceSection stats={perf} />

      <SeasonalSection data={seasonal} />

      <ECSmScatterSection data={ecSmData} />

      <SMTrendSection trend={smTrend} siteId={siteId} />

      <TempTrendSection trend={tempTrend} />

      <ECTrendSection trend={ecTrend} siteId={siteId} />

      <div className="ea-main-row">
        <EventList
          events={events}
          selectedId={selectedId}
          onSelect={handleSelectEvent}
          onDetect={handleDetect}
          detecting={detecting}
          siteId={siteId}
        />
        <EventDetail
          detail={detail}
          loading={loadingDetail}
          siteId={siteId}
          ecTrend={ecTrend}
        />
      </div>
    </div>
  );
}
